import { Oil_Density_Options_Enum } from "gql/api";
import { Rankine } from "../units/temperature";
// import { API, SpecificGravity } from "@/libs/units/density";
export { getPVTFluidProperties } from "./oilPhase/oilPhaseVolumeFactor";

export interface OilDensityBaseParams {
  pressurePSIA: number;
  oilAPI: number;
  temperatureF: number;
  gasSpecificGravity: number;
  bubblePointPressurePSIA: number;
}

export interface DensitySharedCalculations {
  a: number;
  solutionGasAtBubblePoint: number;
  oilCompressibility: number;
}

export interface OilDensityParams extends OilDensityBaseParams {
  oilAPI: number;
  oilSpecificGravity: number;
  gasSolubilitySCFSTB: number;
  pressurePSIA: number;
  oilVolumeFactorBBLSTBr: number;
  temperatureF: number;
}

export function calculateDensityBaseValues({
  pressurePSIA,
  oilAPI,
  temperatureF,
  gasSpecificGravity,
  bubblePointPressurePSIA
}: OilDensityBaseParams): DensitySharedCalculations {
  // Calculate 'a' (Y28)
  // =0.00091*D4-0.0125*D7
  const a = 0.00091 * temperatureF - 0.0125 * oilAPI;

  // Calculate solution gas at bubble point (Y32)
  // =D8*((J15/18.2+1.4)*10^(-Y28))^1.204819
  const solutionGasAtBubblePoint =
    gasSpecificGravity *
    Math.pow(
      (bubblePointPressurePSIA / 18.2 + 1.4) * Math.pow(10, -a),
      1.204819
    );
  // Use same oil compressibility calculation as Standing (Y36)
  // =(-1433+5*Y32+17.2*D13-1180*D8+12.61*D7)/((10^5)*D3)
  const oilCompressibility =
    (-1433 +
      5 * solutionGasAtBubblePoint + // Y32 term
      17.2 * Rankine.toThisUnit(temperatureF) - // D13: In Situ Temperature (651.72)
      1180 * gasSpecificGravity + // D8: Gas SG (0.82)
      12.61 * oilAPI) / // D7: Oil API (35)
    (Math.pow(10, 5) * pressurePSIA); // D3: In Situ Pressure (1933.94)

  return {
    a,
    solutionGasAtBubblePoint,
    oilCompressibility
  };
}
export const getOilFlowRateSTDB = (
  total_liquid_rate_stb_day: number,
  waterCutRatio: number
): number => {
  return total_liquid_rate_stb_day * (1 - waterCutRatio);
};

// returns sigma_oil dy/cm
export function oil_phase_surface_tension(
  oil_api: number,
  pressure: number
): number {
  return Math.max(
    0,
    (39 -
      0.2571 * oil_api -
      ((100 - 68) * (39 - 0.2571 * oil_api - (37.5 - 0.2571 * oil_api))) / 32) *
      (1 - 0.024 * Math.pow(pressure, 0.45))
  );
}

// pressure - P psia
// temperature - T fahrenheit
// returns sigma_water dy/cm
export function water_phase_surface_tension(
  pressure: number,
  tempf: number
): number {
  // This is a wierd calculation
  return (
    0.00000000000000000000142157 * Math.pow(pressure, 6) -
    0.0000000000000000432881 * Math.pow(pressure, 5) +
    0.000000000000475377 * Math.pow(pressure, 4) -
    0.00000000221001 * Math.pow(pressure, 3) +
    0.00000423447 * Math.pow(pressure, 2) -
    0.00870071 * pressure +
    52.0438 -
    ((0.00000000000000000000142157 * Math.pow(pressure, 6) -
      0.0000000000000000432881 * Math.pow(pressure, 5) +
      0.000000000000475377 * Math.pow(pressure, 4) -
      0.00000000221001 * Math.pow(pressure, 3) +
      0.00000423447 * Math.pow(pressure, 2) -
      0.00870071 * pressure +
      52.0438 -
      (0.000000000000000000000784314 * Math.pow(pressure, 6) -
        0.0000000000000000265743 * Math.pow(pressure, 5) +
        0.000000000000356966 * Math.pow(pressure, 4) -
        0.00000000243549 * Math.pow(pressure, 3) +
        0.00000907989 * Math.pow(pressure, 2) -
        0.0195933 * pressure +
        75.9343)) /
      (280 - 74)) *
      (280 - tempf)
  );
}

// oil surface tension - sigma_oil dy/cm
// water surface tension - sigma_water dy/cm
// water cut WC %
// returns sigma_liquids dy/cm
export function liquids_surface_tension(
  oil_st: number,
  water_st: number,
  water_cut: number
): number {
  const stl = water_st * water_cut + oil_st * (1 - water_cut);
  if (stl <= 0) {
    if (water_st > oil_st) {
      return water_st;
    } else {
      return oil_st;
    }
  } else {
    return stl;
  }
}

// D3 → pressurePSIA
// D4 → temperatureF
// D7 → oilAPI
// D8 → gasSpecificGravity
// D15 → oilSpecificGravity
// D20 → oilVolumeFactorBBLSTBr
// J12 → gasSolubilitySCFSTB
// J15/D11 → bubblePointPressurePSIA
// Y32 → solutionGasAtBubblePoint

export function calculateStandingOilDensity(
  params: OilDensityParams,
  sharedCalcs: DensitySharedCalculations
): number {
  const {
    oilSpecificGravity,
    gasSpecificGravity,
    gasSolubilitySCFSTB,
    pressurePSIA,
    bubblePointPressurePSIA,
    temperatureF
  } = params;
  const { solutionGasAtBubblePoint, oilCompressibility } = sharedCalcs;

  // Calculate saturated oil density (Y30)
  // =(D15*62.428+0.0136*J12*D8)/(0.972+0.000147*(J12*(D8/D15)^0.5+1.25*D4)^1.175)
  const saturatedDensity =
    (oilSpecificGravity * 62.428 +
      0.0136 * gasSolubilitySCFSTB * gasSpecificGravity) /
    (0.972 +
      0.000147 *
        Math.pow(
          gasSolubilitySCFSTB *
            Math.pow(gasSpecificGravity / oilSpecificGravity, 0.5) +
            1.25 * temperatureF,
          1.175
        ));

  // Calculate oil density at bubble point (Y34)
  // =(D15*62.428+0.0136*Y32*D8)/(0.972+0.000147*(Y32*(D8/D15)^0.5+1.25*D4)^1.175)
  const oilDensityAtBubblePoint =
    (oilSpecificGravity * 62.428 +
      0.0136 * solutionGasAtBubblePoint * gasSpecificGravity) /
    (0.972 +
      0.000147 *
        Math.pow(
          solutionGasAtBubblePoint *
            Math.pow(gasSpecificGravity / oilSpecificGravity, 0.5) +
            1.25 * temperatureF,
          1.175
        ));

  // Calculate undersaturated oil density (Y38)
  // =Y34*EXP(Y36*(D3-D11))
  const undersaturatedDensity =
    oilDensityAtBubblePoint *
    Math.exp(oilCompressibility * (pressurePSIA - bubblePointPressurePSIA));

  // Return final density based on pressure condition (Y40)
  return pressurePSIA >= bubblePointPressurePSIA
    ? undersaturatedDensity
    : saturatedDensity;
}

export function calculateAhmedOilDensity(
  params: OilDensityParams,
  sharedCalcs: DensitySharedCalculations
): number {
  const {
    oilSpecificGravity,
    gasSpecificGravity,
    gasSolubilitySCFSTB,
    pressurePSIA,
    bubblePointPressurePSIA,
    oilVolumeFactorBBLSTBr,
    temperatureF
  } = params;
  const { solutionGasAtBubblePoint, oilCompressibility } = sharedCalcs;

  // Calculate saturated oil density (Y45)
  // =(62.428*D15+0.0136*J12*D8)/D20
  const saturatedDensity =
    (62.428 * oilSpecificGravity +
      0.0136 * gasSolubilitySCFSTB * gasSpecificGravity) /
    oilVolumeFactorBBLSTBr;

  // Calculate oil formation volume factor at bubble point (Y47)
  // =0.9759+0.00012*(Y32*((D8/D15)^0.5)+1.25*D4)^1.2
  const oilFormationVolumeFactorAtBubblePoint =
    0.9759 +
    0.00012 *
      Math.pow(
        solutionGasAtBubblePoint *
          Math.pow(gasSpecificGravity / oilSpecificGravity, 0.5) +
          1.25 * temperatureF,
        1.2
      );

  // Calculate oil density at bubble point (Y49)
  // =(62.428*D15+0.0136*Y32*D8)/Y47
  const oilDensityAtBubblePoint =
    (62.428 * oilSpecificGravity +
      0.0136 * solutionGasAtBubblePoint * gasSpecificGravity) /
    oilFormationVolumeFactorAtBubblePoint;

  // Calculate undersaturated oil density (Y51)
  // =Y49*EXP(Y36*(D3-J15))
  const undersaturatedDensity =
    oilDensityAtBubblePoint *
    Math.exp(oilCompressibility * (pressurePSIA - bubblePointPressurePSIA));

  // Return final density based on pressure condition (Y53)
  return pressurePSIA >= bubblePointPressurePSIA
    ? undersaturatedDensity
    : saturatedDensity;
}

// File is Fluid.Schutff.Kevin.Edition_with.Density.1.xlsx
export function getOilDensityLbmft3(
  oilAPI: number,
  oilSpecificGravity: number,
  gasSpecificGravity: number,
  gasSolubilitySCFSTB: number,
  temperatureF: number,
  oilDensityCorrelation: Oil_Density_Options_Enum | undefined,
  oilVolumeFactorBBLSTBr: number,
  pressurePSIA: number,
  bubblePointPressurePSIA: number
): number {
  const baseParams = {
    pressurePSIA,
    oilAPI,
    temperatureF,
    gasSpecificGravity,
    bubblePointPressurePSIA
  };

  const sharedCalcs = calculateDensityBaseValues(baseParams);

  const params = {
    ...baseParams,
    oilSpecificGravity,
    gasSolubilitySCFSTB,
    pressurePSIA,
    oilVolumeFactorBBLSTBr
  };

  if (oilDensityCorrelation === Oil_Density_Options_Enum.Standing) {
    return calculateStandingOilDensity(params, sharedCalcs);
  } else if (oilDensityCorrelation === Oil_Density_Options_Enum.Ahmed) {
    return calculateAhmedOilDensity(params, sharedCalcs);
  }

  return 0;
}
